import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { getEvenementByGrouped, markDevisEnvoye } from "state/evenement/actions";
import { fetchGabarit, setSelectedGabarit } from "state/gabarit/actions";
import { setOrderEditionInfo } from "state/orderEdition/actions";
import { appliquerGabaritSurDevis } from "app/orderTool/components/Gabarit/GabaritSelector";

const dateFr = (valeur) => (valeur ? new Date(valeur).toLocaleDateString("fr-FR") : null);

/**
 * Post-it de l'éditeur de devis : notes de la prise de brief de l'événement lié, et bouton
 * « Devis envoyé » qui pose la date d'envoi (relances calées) sans repasser par le pipeline.
 * Rien ne s'affiche si le devis n'a pas de fiche événement. Retours terrain Keep (26/10/2026).
 */
const BriefPostIt = ({ groupedOrderId }) => {
  const dispatch = useDispatch();
  const [evenement, setEvenement] = useState(null);
  const [ouvert, setOuvert] = useState(true);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const orderEdition = useSelector((state) => state.orderEdition);
  const gabaritApplique = useRef(null);

  // Gabarit présélectionné sur la fiche événement : appliqué une fois, à l'ouverture, tant
  // qu'aucune prestation n'a de contenu (un devis déjà travaillé n'est jamais écrasé).
  // En mode brique, le contenu est dans les briques et `items` reste vide au chargement :
  // ne regarder que `items` réappliquait le gabarit à chaque sauvegarde ou F5 (Julie, 05/11/2026).
  useEffect(() => {
    const gabaritId = evenement?.gabarit_id;
    const charge = String(orderEdition?.groupedOrderId || "") === String(groupedOrderId || "");
    const orders = orderEdition?.orders || [];
    const aDuContenu = (o) =>
      (o.items || []).length > 0 || (o.briqueDevis || []).some((b) => (b.items || []).length > 0);
    const vides = orders.length > 0 && !orders.some(aDuContenu);
    if (!gabaritId || !charge || !vides || gabaritApplique.current === `${groupedOrderId}-${gabaritId}`) {
      return;
    }
    gabaritApplique.current = `${groupedOrderId}-${gabaritId}`;
    dispatch(fetchGabarit(gabaritId)).then((gabarit) => {
      if (!gabarit?.data) {
        toast.error("Le gabarit prévu sur la fiche événement est introuvable");
        return;
      }
      dispatch(setOrderEditionInfo(appliquerGabaritSurDevis(orderEdition, gabarit.data)));
      dispatch(setSelectedGabarit(gabarit));
      toast.success(`Gabarit « ${gabarit.name} » de la fiche événement appliqué — pensez à sauvegarder`);
    });
  }, [evenement, orderEdition, groupedOrderId, dispatch]);

  useEffect(() => {
    setEvenement(null);
    if (!groupedOrderId) return;
    let actif = true;
    dispatch(getEvenementByGrouped(groupedOrderId)).then((resp) => {
      if (actif && resp?.evenement) setEvenement(resp.evenement);
    });
    return () => { actif = false; };
  }, [groupedOrderId, dispatch]);

  if (!evenement) return null;

  const marquerEnvoye = async () => {
    setEnvoiEnCours(true);
    const resp = await dispatch(markDevisEnvoye(evenement.id));
    setEnvoiEnCours(false);
    if (resp?.error || resp?.success === false || !resp?.evenement) {
      toast.error(resp?.message || "Impossible de marquer le devis envoyé");
      return;
    }
    setEvenement(resp.evenement);
    toast.success("Devis marqué envoyé : il passe dans « Devis envoyés », relances programmées");
  };

  // Champs du brief utiles à la rédaction du devis, dans l'ordre de lecture.
  const brief = [
    ["Objectif", evenement.objectif],
    ["Déroulé", evenement.deroule_estime],
    ["Contraintes alimentaires", evenement.contraintes_alimentaires],
    ["Boissons", [evenement.boissons_softs, evenement.alcool ? (evenement.alcool_detail || "alcool") : null].filter(Boolean).join(" · ")],
    ["Matériel / mobilier", evenement.materiel_mobilier],
    ["Budget", evenement.budget_centimes ? `${(evenement.budget_centimes / 100).toLocaleString("fr-FR")} €${evenement.budget_base ? ` ${evenement.budget_base}` : ""}` : null],
  ].filter(([, valeur]) => valeur);
  const notes = evenement.notes || [];

  return (
    <div
      style={{
        position: "fixed", right: 16, bottom: 70, zIndex: 1040, width: ouvert ? 320 : "auto",
        background: "#fff8c4", border: "1px solid #e6d77a", borderRadius: 6,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)", fontSize: "0.82rem",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ padding: "6px 10px", cursor: "pointer", borderBottom: ouvert ? "1px solid #e6d77a" : "none" }}
        onClick={() => setOuvert((o) => !o)}
        title={ouvert ? "Réduire" : "Afficher les notes du brief"}
      >
        <b>📌 Brief {evenement.reference}{evenement.nom ? ` — ${evenement.nom}` : ""}</b>
        <span>{ouvert ? "▾" : "▸"}</span>
      </div>
      {ouvert && (
        <div style={{ padding: "6px 10px", maxHeight: 360, overflowY: "auto" }}>
          {brief.map(([libelle, valeur]) => (
            <div key={libelle} className="mb-1">
              <b>{libelle} :</b> <span style={{ whiteSpace: "pre-wrap" }}>{valeur}</span>
            </div>
          ))}
          {notes.length > 0 && (
            <div className="mt-2">
              <b>Notes</b>
              {notes.map((n) => (
                <div key={n.id} className="border-top py-1">
                  <div style={{ fontSize: "0.7rem", color: "#888" }}>{dateFr(n.created_at)}{n.auteur ? ` · ${n.auteur}` : ""}</div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{n.contenu}</div>
                </div>
              ))}
            </div>
          )}
          {brief.length === 0 && notes.length === 0 && (
            <div className="text-muted">Aucune note sur la fiche événement.</div>
          )}
          <div className="mt-2 pt-2 border-top">
            {evenement.date_envoi_devis ? (
              <Badge variant="info">Devis envoyé le {dateFr(evenement.date_envoi_devis)}</Badge>
            ) : (
              <Button size="sm" variant="outline-success" disabled={envoiEnCours} onClick={marquerEnvoye}>
                {envoiEnCours ? "Enregistrement…" : "✉️ Marquer le devis envoyé"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BriefPostIt;
